import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
  FormControlLabel,
  Checkbox,
  Paper,
  Divider,
  Grid,
  Card,
  CardContent,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  ExpandMore as ExpandMoreIcon,
  AccessTime as TimeIcon,
  CalendarToday as CalendarIcon,
} from '@mui/icons-material';
import { DataGrid } from '@mui/x-data-grid';
import { getAuthHeaders } from '../../utils/auth';

const PassTypeManagement = () => {
  // State for pass types and related data
  const [passTypes, setPassTypes] = useState([]);
  const [lessonCategories, setLessonCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Dialog states
  const [createEditDialogOpen, setCreateEditDialogOpen] = useState(false);
  const [viewDetailsDialogOpen, setViewDetailsDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  // Current editing/viewing pass type
  const [editingPassType, setEditingPassType] = useState(null);
  const [viewingPassType, setViewingPassType] = useState(null);
  const [deletingPassType, setDeletingPassType] = useState(null);

  // Form data
  const [formData, setFormData] = useState({
    name: '',
    price: '',
    valid_duration_days: '',
    active: true,
    available_for_purchase: true,
    // Month availability
    month_restricted: false,
    available_months: [],
    // Purchase period availability
    purchase_period_restricted: false,
    purchase_available_from: '',
    purchase_available_until: '',
    // Purchase limit
    purchase_limit: '',
    // Lesson category associations
    lesson_categories_for_pass_type: []
  });

  // Month names for UI
  const monthNames = [
    'Styczeń', 'Luty', 'Marzec', 'Kwiecień', 'Maj', 'Czerwiec',
    'Lipiec', 'Sierpień', 'Wrzesień', 'Październik', 'Listopad', 'Grudzień'
  ];

  // Fetch data on component mount
  useEffect(() => {
    fetchPassTypes();
    fetchLessonCategories();
  }, []);

  const fetchPassTypes = async () => {
    setLoading(true);
    try {
      const headers = await getAuthHeaders();
      // Try to fetch with availability info first
      let response = await fetch(`${process.env.REACT_APP_BACKEND_ADDRESS}/pass-type/with-availability-info`, {
        headers
      });
      
      // Fallback to regular endpoint if the new one fails
      if (!response.ok) {
        response = await fetch(`${process.env.REACT_APP_BACKEND_ADDRESS}/pass-type/`, {
          headers
        });
      }
      
      const data = await response.json();
      if (response.ok) {
        setPassTypes(data.objects || []);
        setError('');
      } else {
        if (response.status === 403 || response.status === 401) {
          setError('Brak uprawnień do pobrania karnetów');
        } else {
          setError('Nie udało się pobrać karnetów');
        }
      }
    } catch (err) {
      setError('Błąd połączenia z serwerem');
      console.error('Error fetching pass types:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchLessonCategories = async () => {
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${process.env.REACT_APP_BACKEND_ADDRESS}/lesson-category/`, {
        headers
      });
      const data = await response.json();
      if (response.ok) {
        setLessonCategories(data.objects || []);
      } else if (response.status === 403 || response.status === 401) {
        console.error('Brak uprawnień do pobrania kategorii lekcji');
      }
    } catch (err) {
      console.error('Error fetching lesson categories:', err);
    }
  };

  // Handle form input changes
  const handleFormChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Handle month selection
  const handleMonthToggle = (monthNumber) => {
    setFormData(prev => ({
      ...prev,
      available_months: prev.available_months.includes(monthNumber)
        ? prev.available_months.filter(m => m !== monthNumber)
        : [...prev.available_months, monthNumber]
    }));
  };

  // Handle lesson category association changes
  const handleLessonCategoryChange = (categoryId, lessons_to_use) => {
    setFormData(prev => ({
      ...prev,
      lesson_categories_for_pass_type: prev.lesson_categories_for_pass_type.map(cat =>
        cat.lesson_category_id === categoryId
          ? { ...cat, lessons_to_use }
          : cat
      )
    }));
  };

  // Handle unlimited toggle
  const handleUnlimitedChange = (categoryId, is_unlimited) => {
    setFormData(prev => ({
      ...prev,
      lesson_categories_for_pass_type: prev.lesson_categories_for_pass_type.map(cat =>
        cat.lesson_category_id === categoryId
          ? { ...cat, is_unlimited, lessons_to_use: is_unlimited ? 0 : (cat.lessons_to_use || 1) }
          : cat
      )
    }));
  };

  // Add/remove lesson category association
  const toggleLessonCategory = (categoryId) => {
    setFormData(prev => {
      const exists = prev.lesson_categories_for_pass_type.find(
        cat => cat.lesson_category_id === categoryId
      );

      if (exists) {
        return {
          ...prev,
          lesson_categories_for_pass_type: prev.lesson_categories_for_pass_type.filter(
            cat => cat.lesson_category_id !== categoryId
          )
        };
      } else {
        return {
          ...prev,
          lesson_categories_for_pass_type: [
            ...prev.lesson_categories_for_pass_type,
            { lesson_category_id: categoryId, lessons_to_use: 1, is_unlimited: false }
          ]
        };
      }
    });
  };

  // Open create dialog
  const handleCreate = () => {
    setFormData({
      name: '',
      price: '',
      valid_duration_days: '',
      active: true,
      month_restricted: false,
      available_months: [],
      purchase_period_restricted: false,
      purchase_available_from: '',
      purchase_available_until: '',
      purchase_limit: '',
      lesson_categories_for_pass_type: []
    });
    setEditingPassType(null);
    setCreateEditDialogOpen(true);
  };

  // Open edit dialog
  const handleEdit = (passType) => {
    setFormData({
      name: passType.name,
      price: passType.price.toString(),
      valid_duration_days: Math.floor(passType.valid_duration / (24 * 60 * 60)).toString(),
      active: passType.active,
      available_for_purchase: passType.available_for_purchase !== undefined ? passType.available_for_purchase : true,
      month_restricted: passType.month_restricted || false,
      available_months: passType.available_months || [],
      purchase_period_restricted: passType.purchase_period_restricted || false,
      purchase_available_from: passType.purchase_available_from ?
        new Date(passType.purchase_available_from).toISOString().slice(0, 16) : '',
      purchase_available_until: passType.purchase_available_until ?
        new Date(passType.purchase_available_until).toISOString().slice(0, 16) : '',
      purchase_limit: passType.purchase_limit ? passType.purchase_limit.toString() : '',
      lesson_categories_for_pass_type: (passType.lesson_categories_for_pass_type || []).map(cat => ({
        ...cat,
        is_unlimited: cat.is_unlimited || false
      }))
    });
    setEditingPassType(passType);
    setCreateEditDialogOpen(true);
  };

  // Handle view details
  const handleView = (passType) => {
    setViewingPassType(passType);
    setViewDetailsDialogOpen(true);
  };

  // Handle delete confirmation
  const handleDelete = (passType) => {
    setDeletingPassType(passType);
    setDeleteDialogOpen(true);
  };

  // Handle form submission (create/edit)
  const handleSubmit = async () => {
    setLoading(true);
    setError('');

    try {
      // Validate form data
      if (!formData.name || !formData.price) {
        setError('Nazwa i cena są wymagane');
        setLoading(false);
        return;
      }

      // Duration is only required if month restriction is not enabled
      if (!formData.month_restricted && !formData.valid_duration_days) {
        setError('Czas trwania jest wymagany gdy nie ma ograniczenia miesięcznego');
        setLoading(false);
        return;
      }

      // Validate month restriction
      if (formData.month_restricted && formData.available_months.length === 0) {
        setError('Proszę wybrać co najmniej jeden miesiąc dla karnetów z ograniczeniem miesięcznym');
        setLoading(false);
        return;
      }

      // Validate purchase period restriction
      if (formData.purchase_period_restricted) {
        if (!formData.purchase_available_from && !formData.purchase_available_until) {
          setError('Proszę określić okres dostępności zakupu');
          setLoading(false);
          return;
        }
        if (formData.purchase_available_from && formData.purchase_available_until &&
            new Date(formData.purchase_available_from) >= new Date(formData.purchase_available_until)) {
          setError('Data rozpoczęcia musi być wcześniejsza niż data zakończenia');
          setLoading(false);
          return;
        }
      }

      // Prepare data for API
      const apiData = {
        name: formData.name,
        price: parseInt(formData.price),
        // Only include valid_duration if it's provided or if month restriction is not enabled
        valid_duration: formData.valid_duration_days ?
          parseInt(formData.valid_duration_days) * 24 * 60 * 60 : // Convert days to seconds
          (formData.month_restricted ? null : 0), // null for month restricted, 0 as fallback
        active: formData.active,
        available_for_purchase: formData.available_for_purchase,
        month_restricted: formData.month_restricted,
        available_months: formData.month_restricted ? formData.available_months : null,
        purchase_period_restricted: formData.purchase_period_restricted,
        purchase_available_from: formData.purchase_period_restricted && formData.purchase_available_from ?
          new Date(formData.purchase_available_from).toISOString() : null,
        purchase_available_until: formData.purchase_period_restricted && formData.purchase_available_until ?
          new Date(formData.purchase_available_until).toISOString() : null,
        purchase_limit: formData.purchase_limit ? parseInt(formData.purchase_limit) : null,
        lesson_categories_for_pass_type: formData.lesson_categories_for_pass_type
      };

      const isEditing = editingPassType !== null;
      const url = isEditing
        ? `${process.env.REACT_APP_BACKEND_ADDRESS}/pass-type/${editingPassType.id}`
        : `${process.env.REACT_APP_BACKEND_ADDRESS}/pass-type/`;

      const method = isEditing ? 'PUT' : 'POST';

      const headers = await getAuthHeaders();
      const response = await fetch(url, {
        method,
        headers: {
          ...headers,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(apiData)
      });

      if (response.ok) {
        setSuccess(isEditing ? 'Karnet zaktualizowany pomyślnie' : 'Karnet utworzony pomyślnie');
        setCreateEditDialogOpen(false);
        await fetchPassTypes(); // Refresh the list
      } else {
        if (response.status === 403 || response.status === 401) {
          setError('Brak uprawnień do zapisania karnetu');
        } else {
          const errorData = await response.json();
          setError(errorData.detail || 'Błąd podczas zapisywania karnetu');
        }
      }
    } catch (err) {
      setError('Błąd połączenia z serwerem');
      console.error('Error saving pass type:', err);
    } finally {
      setLoading(false);
    }
  };

  // Handle actual delete
  const handleConfirmDelete = async () => {
    if (!deletingPassType) return;

    setLoading(true);
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${process.env.REACT_APP_BACKEND_ADDRESS}/pass-type/${deletingPassType.id}`, {
        method: 'DELETE',
        headers
      });

      if (response.ok) {
        setSuccess('Karnet usunięty pomyślnie');
        setDeleteDialogOpen(false);
        await fetchPassTypes(); // Refresh the list
      } else {
        if (response.status === 403 || response.status === 401) {
          setError('Brak uprawnień do usunięcia karnetu');
        } else {
          setError('Nie udało się usunąć karnetu');
        }
      }
    } catch (err) {
      setError('Błąd połączenia z serwerem');
      console.error('Error deleting pass type:', err);
    } finally {
      setLoading(false);
      setDeletingPassType(null);
    }
  };

  // Format duration for display
  const formatDuration = (durationSeconds) => {
    const days = Math.floor(durationSeconds / (24 * 60 * 60));
    return `${days} dni`;
  };

  // Format months for display
  const formatMonths = (months) => {
    if (!months || months.length === 0) return 'Wszystkie miesiące';
    return months.map(m => monthNames[m - 1]).join(', ');
  };

  // Format purchase period for display
  const formatPurchasePeriod = (passType) => {
    if (!passType.purchase_period_restricted) return 'Zawsze dostępny';

    const from = passType.purchase_available_from ?
      new Date(passType.purchase_available_from).toLocaleDateString() : '';
    const until = passType.purchase_available_until ?
      new Date(passType.purchase_available_until).toLocaleDateString() : '';

    if (from && until) return `${from} - ${until}`;
    if (from) return `Od ${from}`;
    if (until) return `Do ${until}`;
    return 'Zawsze dostępny';
  };

  // DataGrid columns
  const columns = [
    { field: 'name', headerName: 'Nazwa', flex: 1, minWidth: 200 },
    {
      field: 'price',
      headerName: 'Cena',
      width: 100,
      renderCell: (params) => `${params.value} zł`
    },
    {
      field: 'valid_duration',
      headerName: 'Czas trwania',
      width: 120,
      renderCell: (params) => formatDuration(params.value)
    },
    {
      field: 'active',
      headerName: 'Aktywny',
      width: 100,
      renderCell: (params) => (
        <Chip
          label={params.value ? 'Tak' : 'Nie'}
          color={params.value ? 'success' : 'error'}
          size="small"
        />
      )
    },
    {
      field: 'month_restricted',
      headerName: 'Ograniczenie miesięczne',
      width: 180,
      renderCell: (params) => params.row.month_restricted ? (
        <Chip
          icon={<CalendarIcon />}
          label="Ograniczone"
          color="warning"
          size="small"
        />
      ) : (
        <Chip
          label="Bez ograniczeń"
          color="default"
          size="small"
        />
      )
    },
    {
      field: 'purchase_period_restricted',
      headerName: 'Okres zakupu',
      width: 150,
      renderCell: (params) => params.row.purchase_period_restricted ? (
        <Chip
          icon={<TimeIcon />}
          label="Ograniczony"
          color="info"
          size="small"
        />
      ) : (
        <Chip
          label="Zawsze"
          color="default"
          size="small"
        />
      )
    },
    {
      field: 'purchase_limit',
      headerName: 'Limit zakupów',
      width: 130,
      renderCell: (params) => {
        const availabilityInfo = params.row.availability_info;
        if (availabilityInfo && availabilityInfo.has_purchase_limit) {
          const { current_active_count, purchase_limit, is_available } = availabilityInfo;
          return (
            <Box>
              <Typography variant="body2" sx={{ 
                color: is_available ? 'text.primary' : 'error.main',
                fontWeight: is_available ? 'normal' : 'bold'
              }}>
                {current_active_count}/{purchase_limit}
              </Typography>
              {!is_available && (
                <Typography variant="caption" color="error">
                  LIMIT
                </Typography>
              )}
            </Box>
          );
        }
        return (
          <Typography variant="body2">
            Bez limitu
          </Typography>
        );
      }
    },
    {
      field: 'actions',
      headerName: 'Akcje',
      width: 150,
      sortable: false,
      renderCell: (params) => (
        <Box>
          <IconButton onClick={() => handleView(params.row)} size="small">
            <VisibilityIcon />
          </IconButton>
          <IconButton onClick={() => handleEdit(params.row)} size="small">
            <EditIcon />
          </IconButton>
          <IconButton onClick={() => handleDelete(params.row)} size="small" color="error">
            <DeleteIcon />
          </IconButton>
        </Box>
      ),
    },
  ];

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h5" component="h2" gutterBottom>
            Zarządzanie Karnetami
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Tworzenie i edycja typów karnetów z możliwością ograniczenia dostępności do konkretnych miesięcy i okresów
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleCreate}
          sx={{ textTransform: 'none' }}
        >
          Nowy Karnet
        </Button>
      </Box>

      {/* Error/Success alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* DataGrid */}
      <Paper sx={{ height: 600, width: '100%' }}>
        <DataGrid
          rows={passTypes}
          columns={columns}
          pageSize={10}
          rowsPerPageOptions={[5, 10, 25]}
          loading={loading}
          disableSelectionOnClick
          sx={{
            '& .MuiDataGrid-cell:hover': {
              backgroundColor: '#f5f5f5',
            },
          }}
        />
      </Paper>

      {/* Create/Edit Dialog */}
      <Dialog
        open={createEditDialogOpen}
        onClose={() => setCreateEditDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { maxHeight: '90vh', overflow: 'hidden' }
        }}
      >
        <DialogTitle>
          {editingPassType ? 'Edytuj Karnet' : 'Nowy Karnet'}
        </DialogTitle>
        <DialogContent sx={{ overflowY: 'auto' }}>
          <Box sx={{ pt: 2 }}>
            {/* Basic Information */}
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Podstawowe informacje
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Nazwa karnetu"
                      value={formData.name}
                      onChange={(e) => handleFormChange('name', e.target.value)}
                      margin="normal"
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      label="Cena (zł)"
                      type="number"
                      value={formData.price}
                      onChange={(e) => handleFormChange('price', e.target.value)}
                      margin="normal"
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      label={formData.month_restricted ? "Czas trwania (dni) - opcjonalne" : "Czas trwania (dni)"}
                      type="number"
                      value={formData.valid_duration_days}
                      onChange={(e) => handleFormChange('valid_duration_days', e.target.value)}
                      margin="normal"
                      helperText={formData.month_restricted ? "Pozostaw puste aby automatycznie obliczyć na podstawie wybranych miesięcy" : ""}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      label="Limit zakupów (opcjonalne)"
                      type="number"
                      value={formData.purchase_limit}
                      onChange={(e) => handleFormChange('purchase_limit', e.target.value)}
                      margin="normal"
                      helperText="Pozostaw puste aby zezwolić na nieograniczoną liczbę zakupów"
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formData.active}
                          onChange={(e) => handleFormChange('active', e.target.checked)}
                        />
                      }
                      label="Karnet aktywny (widoczny w cenniku)"
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formData.available_for_purchase}
                          onChange={(e) => handleFormChange('available_for_purchase', e.target.checked)}
                        />
                      }
                      label="Dostępny do zakupu (w StoreModal)"
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* Month Availability Section */}
            <Accordion sx={{ mb: 2 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box display="flex" alignItems="center">
                  <CalendarIcon sx={{ mr: 1, color: 'primary.main' }} />
                  <Typography variant="h6">
                    Ograniczenie dostępności do konkretnych miesięcy
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.month_restricted}
                      onChange={(e) => handleFormChange('month_restricted', e.target.checked)}
                      color="primary"
                    />
                  }
                  label="Ograniczyć dostępność do wybranych miesięcy"
                  sx={{ mb: 2 }}
                />

                {formData.month_restricted && (
                  <Box>
                    <Typography variant="subtitle2" gutterBottom>
                      Wybierz dostępne miesiące:
                    </Typography>
                    <Grid container spacing={1}>
                      {monthNames.map((monthName, index) => (
                        <Grid item xs={6} sm={4} md={3} key={index + 1}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                size="small"
                                checked={formData.available_months.includes(index + 1)}
                                onChange={() => handleMonthToggle(index + 1)}
                                color="primary"
                              />
                            }
                            label={monthName}
                            sx={{ fontSize: '0.875rem' }}
                          />
                        </Grid>
                      ))}
                    </Grid>
                    {formData.available_months.length > 0 && (
                      <Alert severity="info" sx={{ mt: 2 }}>
                        Wybrane miesiące: {formData.available_months.map(m => monthNames[m - 1]).join(', ')}
                      </Alert>
                    )}
                  </Box>
                )}
              </AccordionDetails>
            </Accordion>

            {/* Purchase Period Section */}
            <Accordion sx={{ mb: 2 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box display="flex" alignItems="center">
                  <TimeIcon sx={{ mr: 1, color: 'info.main' }} />
                  <Typography variant="h6">
                    Ograniczenie okresu zakupu
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.purchase_period_restricted}
                      onChange={(e) => handleFormChange('purchase_period_restricted', e.target.checked)}
                      color="primary"
                    />
                  }
                  label="Ograniczyć okres w którym można kupić karnet"
                  sx={{ mb: 2 }}
                />

                {formData.purchase_period_restricted && (
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Dostępny od"
                        type="datetime-local"
                        value={formData.purchase_available_from}
                        onChange={(e) => handleFormChange('purchase_available_from', e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        margin="normal"
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Dostępny do"
                        type="datetime-local"
                        value={formData.purchase_available_until}
                        onChange={(e) => handleFormChange('purchase_available_until', e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        margin="normal"
                      />
                    </Grid>
                  </Grid>
                )}
              </AccordionDetails>
            </Accordion>

            {/* Lesson Categories Section */}
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Kategorie zajęć
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Wybierz kategorie zajęć, na które będzie można używać tego karnetu
                </Typography>

                {lessonCategories.map(category => {
                  const isSelected = formData.lesson_categories_for_pass_type.find(
                    cat => cat.lesson_category_id === category.id
                  );

                  return (
                    <Box key={category.id} sx={{ mb: 2 }}>
                      <Grid container spacing={2} alignItems="center">
                        <Grid item xs={6}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={!!isSelected}
                                onChange={() => toggleLessonCategory(category.id)}
                                color="primary"
                              />
                            }
                            label={category.name}
                          />
                        </Grid>
                        {isSelected && (
                          <>
                            <Grid item xs={3}>
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    checked={isSelected.is_unlimited || false}
                                    onChange={(e) => handleUnlimitedChange(category.id, e.target.checked)}
                                    color="secondary"
                                    size="small"
                                  />
                                }
                                label="Bez limitu"
                                sx={{ fontSize: '0.875rem' }}
                              />
                            </Grid>
                            <Grid item xs={3}>
                              {!isSelected.is_unlimited && (
                                <TextField
                                  fullWidth
                                  size="small"
                                  label="Liczba lekcji"
                                  type="number"
                                  value={isSelected.lessons_to_use}
                                  onChange={(e) => handleLessonCategoryChange(category.id, parseInt(e.target.value) || 1)}
                                  inputProps={{ min: 1 }}
                                />
                              )}
                              {isSelected.is_unlimited && (
                                <Typography variant="body2" color="primary" sx={{ mt: 1 }}>
                                  Nieograniczone lekcje
                                </Typography>
                              )}
                            </Grid>
                          </>
                        )}
                      </Grid>
                    </Box>
                  );
                })}
              </CardContent>
            </Card>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateEditDialogOpen(false)} disabled={loading}>
            Anuluj
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : null}
          >
            {loading ? 'Zapisywanie...' : (editingPassType ? 'Aktualizuj' : 'Utwórz')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Details Dialog */}
      <Dialog
        open={viewDetailsDialogOpen}
        onClose={() => setViewDetailsDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Typography variant="h6">
              Szczegóły Karnetu
            </Typography>
            <Box display="flex" gap={1}>
              <IconButton onClick={() => handleEdit(viewingPassType)} color="primary">
                <EditIcon />
              </IconButton>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent>
          {viewingPassType && (
            <Box>
              {/* Basic Information Card */}
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h5" gutterBottom>
                    {viewingPassType.name}
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        Cena
                      </Typography>
                      <Typography variant="h6">
                        {viewingPassType.price} zł
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        Czas trwania
                      </Typography>
                      <Typography variant="h6">
                        {formatDuration(viewingPassType.valid_duration)}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        Status
                      </Typography>
                      <Chip
                        label={viewingPassType.active ? 'Aktywny' : 'Nieaktywny'}
                        color={viewingPassType.active ? 'success' : 'error'}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        Limit zakupów
                      </Typography>
                      <Typography variant="h6">
                        {viewingPassType.purchase_limit ? `Maksymalnie ${viewingPassType.purchase_limit}` : 'Bez limitu'}
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              {/* Availability Information */}
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    <CalendarIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Dostępność
                  </Typography>

                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Ograniczenie miesięczne:
                    </Typography>
                    {viewingPassType.month_restricted ? (
                      <Alert severity="warning" sx={{ mt: 1 }}>
                        Dostępny tylko w miesiącach: {formatMonths(viewingPassType.available_months)}
                      </Alert>
                    ) : (
                      <Typography variant="body2">
                        Dostępny przez cały rok
                      </Typography>
                    )}
                  </Box>

                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Okres zakupu:
                    </Typography>
                    {viewingPassType.purchase_period_restricted ? (
                      <Alert severity="info" sx={{ mt: 1 }}>
                        {formatPurchasePeriod(viewingPassType)}
                      </Alert>
                    ) : (
                      <Typography variant="body2">
                        Zawsze dostępny do zakupu
                      </Typography>
                    )}
                  </Box>
                </CardContent>
              </Card>

              {/* Lesson Categories */}
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Kategorie zajęć
                  </Typography>
                  {viewingPassType.lesson_categories_for_pass_type &&
                   viewingPassType.lesson_categories_for_pass_type.length > 0 ? (
                    <Grid container spacing={2}>
                      {viewingPassType.lesson_categories_for_pass_type.map((category, index) => {
                        const categoryName = lessonCategories.find(
                          cat => cat.id === category.lesson_category_id
                        )?.name || `Kategoria ID: ${category.lesson_category_id}`;

                        return (
                          <Grid item xs={12} sm={6} key={index}>
                            <Paper sx={{ p: 2, backgroundColor: '#f5f5f5' }}>
                              <Typography variant="subtitle1" gutterBottom>
                                {categoryName}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                {category.is_unlimited ? 'Nieograniczone lekcje' : `Liczba lekcji: ${category.lessons_to_use}`}
                              </Typography>
                              {category.is_unlimited && (
                                <Chip
                                  label="UNLIMITED"
                                  color="success"
                                  size="small"
                                  sx={{ mt: 1 }}
                                />
                              )}
                            </Paper>
                          </Grid>
                        );
                      })}
                    </Grid>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      Brak przypisanych kategorii zajęć
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDetailsDialogOpen(false)}>
            Zamknij
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Potwierdź usunięcie</DialogTitle>
        <DialogContent>
          <Typography>
            Czy na pewno chcesz usunąć karnet "{deletingPassType?.name}"?
          </Typography>
          <Alert severity="warning" sx={{ mt: 2 }}>
            Ta operacja jest nieodwracalna. Wszystkie istniejące karnety tego typu pozostaną,
            ale nie będzie można tworzyć nowych.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={loading}>
            Anuluj
          </Button>
          <Button
            color="error"
            onClick={handleConfirmDelete}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : null}
          >
            {loading ? 'Usuwanie...' : 'Usuń'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PassTypeManagement;
